import { Component, forwardRef, lazy, type ReactNode, Suspense } from "react";
import type { RichComposerHandle, RichComposerProps } from "../rich-composer";

// The rich message box, loaded on first use so the editor never weighs on starting the app. While
// it loads, or if it cannot load, the fallback shows instead, which is the plain message box.

const Loaded = /* @__PURE__ */ lazy(() =>
  import("../rich-composer").then((m) => ({ default: m.RichComposer })),
);

class LoadFailed extends Component<{ fallback: ReactNode; children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

/**
 * Whether the editor can run here. It reads the platform and user agent to choose its keys, and a
 * page or test that leaves them out would make it fail as it starts. The plain box serves there.
 */
function editorCanRun(): boolean {
  return (
    typeof navigator !== "undefined" &&
    typeof navigator.platform === "string" &&
    typeof navigator.userAgent === "string"
  );
}

/** Starts loading the editor early, such as when the chat list shows, so it is ready on open. */
export function preloadRichComposer(): Promise<unknown> {
  return import("../rich-composer");
}

export type RichComposerLazyProps = RichComposerProps & {
  /** Shown while the editor loads and if it fails to load: the plain message box. */
  fallback: ReactNode;
};

export const RichComposerLazy = /* @__PURE__ */ forwardRef<RichComposerHandle, RichComposerLazyProps>(
  function RichComposerLazy({ fallback, ...props }, ref) {
    if (!editorCanRun()) return <>{fallback}</>;
    return (
      <LoadFailed fallback={fallback}>
        <Suspense fallback={fallback}>
          <Loaded ref={ref} {...props} />
        </Suspense>
      </LoadFailed>
    );
  },
);

export type { ChannelHit, ComposerValue, PersonHit } from "../rich-composer";
export type { RichComposerHandle, RichComposerProps };
